import React, { useState, useEffect } from 'react';
import './about.css';
import { useOutletContext } from 'react-router-dom';
import {
  FiGlobe, FiCheckCircle, FiBriefcase, FiUsers,
  FiCalendar, FiMapPin, FiStar, FiHome, FiUser,
  FiBook, FiAward
} from 'react-icons/fi';

const ABOUT_CACHE_TTL = 5 * 60 * 1000;
const _aboutCache = new Map();

const cacheGet = (key) => {
  const entry = _aboutCache.get(key);
  return entry && Date.now() < entry.exp ? entry.val : null;
};

const cacheSet = (key, value) => {
  _aboutCache.set(key, { val: value, exp: Date.now() + ABOUT_CACHE_TTL });
};

// localStorage with TTL — persists across tabs/reloads, key matches Store.js
const storageGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { val, exp } = JSON.parse(raw);
    return Date.now() < exp ? val : null;
  } catch { return null; }
};

const storageSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify({ val: value, exp: Date.now() + ABOUT_CACHE_TTL }));
  } catch {}
};

const getAboutCacheKey = (pageId) => `about:${pageId}`;

const readCachedAbout = (pageId) => {
  if (!pageId) return null;
  const key = getAboutCacheKey(pageId);
  return cacheGet(key) || storageGet(key);
};

const TypeBadge = ({ isFirm }) => (
  <span className={`about-type-badge ${isFirm ? 'firm' : 'individual'}`}>
    {isFirm ? '🏢 Business' : '👤 Individual'}
  </span>
);

const InfoRow = ({ icon, label, value, isLink, index }) => (
  <div className="about-info-row" style={{ animationDelay: `${index * 0.04}s` }}>
    <div className="about-info-icon">{icon}</div>
    <div className="about-info-body">
      <span className="about-info-label">{label}</span>
      {isLink ? (
        <a
          href={value.startsWith('http') ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="about-info-link"
        >
          {value}
        </a>
      ) : (
        <span className="about-info-value">{value}</span>
      )}
    </div>
  </div>
);

const SectionHead = ({ title }) => (
  <div className="about-section-head">{title}</div>
);

function About() {
  const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
  const { pageId, aboutData: ctxAboutData } = useOutletContext();

  // Priority: Outlet context → localStorage → module Map cache → nothing
  const initialCache = (() => {
    if (ctxAboutData) {
      // Parse the raw API shape from context into the {content, pageType} shape
      const content = {
        paragraphs:    ctxAboutData.paragraphs    || [],
        website:       ctxAboutData.website       || "",
        verified_page: ctxAboutData.verified_page || "",
        industry:      ctxAboutData.industry      || "",
        company:       ctxAboutData.company       || "",
        headquarter:   ctxAboutData.headquarter   || "",
        founded:       ctxAboutData.founded       || "",
        specialties:   ctxAboutData.specialties   || "",
        address:       ctxAboutData.address       || "",
        country:       ctxAboutData.country       || "",
        state:         ctxAboutData.state         || "",
        city:          ctxAboutData.city          || "",
        zipCode:       ctxAboutData.zipcode       || "",
      };
      return { content, pageType: ctxAboutData.page_type || "" };
    }
    return readCachedAbout(pageId);
  })();

  const [content, setContent] = useState(initialCache?.content || null);
  const [pageType, setPageType] = useState(initialCache?.pageType || "");
  const [loading, setLoading] = useState(initialCache ? false : true);

  // True when Store's eager prefetch already delivered fresh about data.
  // In that case we skip the fetch entirely on first mount.
  const ctxIsFresh = Boolean(ctxAboutData);

  useEffect(() => {
    if (!pageId) return;
    if (ctxIsFresh) {
      // Store already gave us fresh data via Outlet context — no fetch needed.
      // Persist it so it's available on next reload before the network responds.
      const cacheEntry = { content: initialCache.content, pageType: initialCache.pageType };
      cacheSet(getAboutCacheKey(pageId), cacheEntry);
      storageSet(getAboutCacheKey(pageId), cacheEntry);
      // Revalidate in background after 30s — user sees content instantly.
      const id = setTimeout(() => fetchAboutPage(pageId, true), 30_000);
      return () => clearTimeout(id);
    }
    const cached = readCachedAbout(pageId);
    if (cached) {
      setContent(cached.content);
      setPageType(cached.pageType);
      setLoading(false);
      fetchAboutPage(pageId, true);
    } else {
      fetchAboutPage(pageId);
    }
  }, [pageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAboutPage = async (currentPageId, background = false) => {
    if (!currentPageId) return;
    if (!background) setLoading(true);
    try {
      const response = await fetch(`${API_URL}/get_about_page_for_store?pageid=${pageId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const aboutContent = {
        paragraphs:    data.paragraphs    || [],
        website:       data.website       || "",
        verified_page: data.verified_page || "",
        industry:      data.industry      || "",
        company:       data.company       || "",
        headquarter:   data.headquarter   || "",
        founded:       data.founded       || "",
        specialties:   data.specialties   || "",
        address:       data.address       || "",
        country:       data.country       || "",
        state:         data.state         || "",
        city:          data.city          || "",
        zipCode:       data.zipcode       || "",
      };
      const pageTypeValue = data.page_type || "";
      setPageType(pageTypeValue);
      setContent(aboutContent);
      const cacheEntry = { content: aboutContent, pageType: pageTypeValue };
      cacheSet(getAboutCacheKey(currentPageId), cacheEntry);
      storageSet(getAboutCacheKey(currentPageId), cacheEntry);
    } catch (error) {
      console.error('fetchAboutPage - Error:', error);
      if (!background) setContent({});
    } finally {
      if (!background) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="about_container">
        <div className="about_content">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="about-info-row">
              <div className="about-skeleton circle" />
              <div className="about-info-body" style={{ gap: 6 }}>
                <div className="about-skeleton line short" />
                <div className="about-skeleton line medium" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!content) return null;

  const isFirm = pageType === "Firm";

  // Build address string
  const addressParts = [
    content.address,
    content.city,
    content.state,
    content.zipCode,
    content.country,
  ].filter(Boolean);
  const fullAddress = addressParts.join(', ');

  // ── FIRM rows — only include if value exists ─────
  const firmRows = [
    { icon: <FiGlobe size={15} />,       label: 'Website',      value: content.website,       isLink: true },
    { icon: <FiCheckCircle size={15} />, label: 'Verified',     value: content.verified_page               },
    { icon: <FiBriefcase size={15} />,   label: 'Industry',     value: content.industry                    },
    { icon: <FiUsers size={15} />,       label: 'Company Size', value: content.company                     },
    { icon: <FiCalendar size={15} />,    label: 'Founded',      value: content.founded                     },
    { icon: <FiHome size={15} />,        label: 'Headquarters', value: content.headquarter                 },
    { icon: <FiStar size={15} />,        label: 'Specialties',  value: content.specialties                 },
    { icon: <FiMapPin size={15} />,      label: 'Address',      value: fullAddress                         },
  ].filter(row => row.value); // ← hide empty rows

  // ── INDIVIDUAL rows — only include if value exists
  const individualRows = [
    { icon: <FiGlobe size={15} />,       label: 'Website / Portfolio', value: content.website,       isLink: true },
    { icon: <FiCheckCircle size={15} />, label: 'Verified',            value: content.verified_page               },
    { icon: <FiUser size={15} />,        label: 'Profession',          value: content.industry                    },
    { icon: <FiBriefcase size={15} />,   label: 'Occupation',          value: content.company                     },
    { icon: <FiBook size={15} />,        label: 'Education',           value: content.headquarter                 },
    { icon: <FiAward size={15} />,       label: 'Skills',              value: content.specialties                 },
    { icon: <FiCalendar size={15} />,    label: 'Member Since',        value: content.founded                     },
    { icon: <FiMapPin size={15} />,      label: 'Location',            value: fullAddress                         },
  ].filter(row => row.value); // ← hide empty rows

  const rows = isFirm ? firmRows : individualRows;

  // Nothing to show at all
  const hasAnything = content.paragraphs.length > 0 || rows.length > 0;

  if (!hasAnything) {
    return (
      <div className="about_container">
        <div className="about_content">
          <div className="header-container">
            <h2>Overview</h2>
            <TypeBadge isFirm={isFirm} />
          </div>
          <div className="about-empty-state">
            <span>📋</span>
            <p>No about information added yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="about_container">
      <div className="about_content">

        {/* Header */}
        <div className="header-container">
          <h2>Overview</h2>
          <TypeBadge isFirm={isFirm} />
        </div>

        {/* Paragraphs / bio */}
        {content.paragraphs.length > 0 && (
          <div className="about-paragraphs">
            {content.paragraphs.map((para, index) => (
              <div key={index} className="paragraph-container">
                <p className="para">{para}</p>
              </div>
            ))}
          </div>
        )}

        {/* Only show section + rows if there's something to show */}
        {rows.length > 0 && (
          <>
            <SectionHead title={isFirm ? 'Business Details' : 'Personal Details'} />
            <div className="about-info-grid">
              {rows.map(({ icon, label, value, isLink }, idx) => (
                <InfoRow
                  key={label}
                  icon={icon}
                  label={label}
                  value={value}
                  isLink={isLink}
                  index={idx}
                />
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default About;